import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { BANNER_IDS } from "@slice/data/constants";
import { useMeQuery } from "@slice/indexer";
import { useIsClient } from "@uidotdev/usehooks";
import { memo, useCallback, useEffect } from "react";
import { Outlet, useLocation } from "react-router";
import { Toaster, type ToasterProps } from "sonner";
import FullPageLoader from "@/components/Shared/FullPageLoader";
import GlobalAlerts from "@/components/Shared/GlobalAlerts";
import GlobalModals from "@/components/Shared/GlobalModals";
import GlobalShortcuts from "@/components/Shared/GlobalShortcuts";
import Navbar from "@/components/Shared/Navbar";
import BottomNavigation from "@/components/Shared/Navbar/BottomNavigation";
import { Spinner } from "@/components/Shared/UI";
import reloadAllTabs from "@/helpers/reloadAllTabs";
import { useTheme } from "@/hooks/useTheme";
import { useAccountStore } from "@/store/persisted/useAccountStore";
import { hydrateAuthTokens, signOut } from "@/store/persisted/useAuthStore";
import { useProStore } from "@/store/persisted/useProStore";
import PageviewTracker from "./PageviewTracker";
import ReloadTabsWatcher from "./ReloadTabsWatcher";

const Layout = () => {
  useLocation();
  const { theme } = useTheme();
  const { currentAccount, setCurrentAccount } = useAccountStore();
  const { setProBannerDismissed } = useProStore();
  const isMounted = useIsClient();
  const { accessToken } = hydrateAuthTokens();

  // Disable scroll restoration on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onError = useCallback(() => {
    signOut();
    reloadAllTabs();
  }, []);

  const { loading } = useMeQuery({
    onCompleted: ({ me, proBanner }) => {
      setCurrentAccount(me.loggedInAs.account);
      if (proBanner?.__typename === "Post") {
        setProBannerDismissed(proBanner.operations?.dismissed ?? false);
      }
    },
    onError,
    skip: !accessToken,
    variables: { proBannerId: BANNER_IDS.PRO }
  });

  const accountLoading = !currentAccount && loading;

  if (accountLoading || !isMounted) {
    return <FullPageLoader />;
  }

  return (
    <>
      {accessToken ? <PageviewTracker /> : null}
      <Toaster
        icons={{
          error: <XCircleIcon className="size-5" />,
          loading: <Spinner size="xs" className="min-w-[16px] w-4 h-4" />,
          success: <CheckCircleIcon className="size-5" />
        }}
        position="bottom-right"
        theme={theme as ToasterProps["theme"]}
        toastOptions={{
          className: "font-sofia-pro",
          style: { 
            boxShadow: "none", 
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px"
          }
        }}
      />
      <GlobalShortcuts />
      <GlobalModals />
      <GlobalAlerts />
      <ReloadTabsWatcher />
      <div className="mx-auto flex w-full max-w-full sm:max-w-5xl px-0 sm:px-2 items-start gap-x-8 md:px-5">
        <Navbar />
        <Outlet />
        <BottomNavigation />
      </div>
    </>
  );
};

export default memo(Layout);
